import {MutableRefObject} from 'react';

const PNG_MIME = 'image/png';

export type CopyImageSource =
    | Blob
    | Promise<Blob>
    | MutableRefObject<Blob | null | undefined>;

export class CopyImageInFlightError extends Error {
    constructor() {
        super('Копирование изображения уже выполняется');
        this.name = 'CopyImageInFlightError';
    }
}

export const isCopyImageInFlightError = (error: unknown): boolean =>
    error instanceof CopyImageInFlightError;

export const isClipboardImageWriteSupported = (): boolean =>
    typeof navigator !== 'undefined'
    && Boolean(navigator.clipboard?.write)
    && typeof ClipboardItem !== 'undefined';

let inFlight: Promise<void> | null = null;

const isPromiseLike = (value: unknown): value is Promise<Blob> =>
    typeof value === 'object'
    && value !== null
    && typeof (value as Promise<Blob>).then === 'function';

const isBlobRef = (value: CopyImageSource): value is MutableRefObject<Blob | null | undefined> =>
    typeof value === 'object'
    && value !== null
    && !(value instanceof Blob)
    && !isPromiseLike(value)
    && 'current' in value;

const unwrapClipboardSource = (source: CopyImageSource): Blob | Promise<Blob> => {
    if (source instanceof Blob) {
        return source;
    }

    if (isPromiseLike(source)) {
        return source;
    }

    if (isBlobRef(source)) {
        const current = source.current;

        if (!(current instanceof Blob)) {
            throw new Error('Источник изображения пуст');
        }

        return current;
    }

    throw new Error('Источник изображения пуст');
};

const convertBlobToPng = async (blob: Blob): Promise<Blob> => {
    if (blob.type === PNG_MIME) {
        return blob;
    }

    const bitmap = await createImageBitmap(blob);
    const canvas = document.createElement('canvas');
    canvas.width = bitmap.width;
    canvas.height = bitmap.height;

    const ctx = canvas.getContext('2d');

    if (!ctx) {
        bitmap.close();
        throw new Error('Canvas 2D context unavailable');
    }

    ctx.drawImage(bitmap, 0, 0);
    bitmap.close();

    return new Promise<Blob>((resolve, reject) => {
        canvas.toBlob(
            (result) => (result ? resolve(result) : reject(new Error('PNG conversion failed'))),
            PNG_MIME,
        );
    });
};

const ensurePngBlob = (blob: Blob): Promise<Blob> => {
    if (!(blob instanceof Blob)) {
        return Promise.reject(new Error('Источник изображения пуст'));
    }

    return convertBlobToPng(blob);
};

const writePngToClipboard = async (png: Blob | Promise<Blob>): Promise<void> => {
    await navigator.clipboard.write([
        new ClipboardItem({
            [PNG_MIME]: png,
        }),
    ]);
};

const writeImage = async (source: Blob | Promise<Blob>): Promise<void> => {
    if (source instanceof Blob && source.type === PNG_MIME) {
        await writePngToClipboard(source);
        return;
    }

    const pngPromise = Promise.resolve(source).then(ensurePngBlob);

    try {
        await writePngToClipboard(pngPromise);
    } catch (firstError) {
        const pngBlob = await pngPromise;
        try {
            await writePngToClipboard(pngBlob);
        } catch {
            throw firstError;
        }
    }
};

export const copyImageToClipboard = (source: CopyImageSource): Promise<void> => {
    if (inFlight) {
        return Promise.reject(new CopyImageInFlightError());
    }

    if (!isClipboardImageWriteSupported()) {
        return Promise.reject(new Error('Копирование изображения не поддерживается'));
    }

    let resolved: Blob | Promise<Blob>;

    try {
        resolved = unwrapClipboardSource(source);
    } catch (error) {
        return Promise.reject(error);
    }

    const task = writeImage(resolved).finally(() => {
        if (inFlight === task) {
            inFlight = null;
        }
    });

    inFlight = task;
    return task;
};
